/**
 * Locale-aware formatting
 *
 * Every date, time, number and percentage the user sees should be formatted
 * through this module. It reads the active language from i18next, so switching
 * language in Settings reformats values without any call site knowing.
 *
 * Before this existed, relative time was reimplemented in four screens with
 * English suffixes baked in ("2h ago"), and three call sites hardcoded 'en-US'.
 * Those are the cases these helpers replace.
 *
 * Intl is available natively on Hermes (RN 0.81) and in every browser we
 * target, so nothing here needs a polyfill.
 */

import i18n, { DEFAULT_LANGUAGE, t } from '../../i18n';

/** The language i18next is currently rendering in. */
function activeLocale() {
  return i18n.language || DEFAULT_LANGUAGE;
}

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Relative-time presets.
 *
 * These reproduce the four hand-rolled variants that previously lived in
 * AdminReportDetails, ActivityTimeline, ReportSummaryRow and AttachmentList.
 * They differ genuinely — in granularity, in how long before falling back to an
 * absolute date, and in abbreviation style — so they are preserved rather than
 * silently unified. Collapsing them into one house style is a product decision,
 * not a refactor.
 *
 *   compact  "2h ago" / "3d ago", absolute date after a week
 *   timeline "5m ago" through "29d ago", absolute date after a month
 *   verbose  "2 hours ago", absolute date after a day
 *
 * `midSentence` picks the lower-case "just now" wording for callers that embed
 * the result inside a sentence ("Updated just now"). Only the just-now case is
 * affected — Intl already returns lower-case relative times, and the absolute
 * date fallback is a number. Each language supplies both forms, so this stays
 * correct where capitalization rules differ from English.
 */
export const RELATIVE_TIME_PRESETS = {
  compact: { style: 'narrow', showMinutes: false, maxDays: 7, midSentence: false },
  timeline: { style: 'narrow', showMinutes: true, maxDays: 30, midSentence: false },
  verbose: { style: 'long', showMinutes: false, maxDays: 1, midSentence: false },
};

/**
 * Format a timestamp as time elapsed, falling back to an absolute date once the
 * gap is wide enough that "43d ago" stops being useful.
 *
 * @param {number|string|Date} timestamp - anything the Date constructor accepts
 * @param {string|Object} [preset='compact'] - a RELATIVE_TIME_PRESETS key, or an
 *   options object of the same shape
 * @returns {string} localized relative time, or '' for a missing timestamp
 */
export function formatRelativeTime(timestamp, preset = 'compact') {
  if (timestamp === null || timestamp === undefined || timestamp === '') return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const options =
    typeof preset === 'string'
      ? RELATIVE_TIME_PRESETS[preset] || RELATIVE_TIME_PRESETS.compact
      : { ...RELATIVE_TIME_PRESETS.compact, ...preset };

  const { style, showMinutes, maxDays, midSentence } = options;
  const justNow = midSentence ? t('common:time.justNowMidSentence') : t('common:time.justNow');
  const elapsed = Date.now() - date.getTime();

  // Future timestamps (clock skew, scheduled items) read oddly as "in 3 hours"
  // next to a list of past events, so treat them as the present moment.
  if (elapsed < 0) return justNow;

  const minutes = Math.floor(elapsed / MS_PER_MINUTE);
  const hours = Math.floor(elapsed / MS_PER_HOUR);
  const days = Math.floor(elapsed / MS_PER_DAY);

  if (showMinutes ? minutes < 1 : hours < 1) return justNow;

  const formatter = new Intl.RelativeTimeFormat(activeLocale(), { numeric: 'always', style });

  if (showMinutes && minutes < 60) return formatter.format(-minutes, 'minute');
  if (hours < 24) return formatter.format(-hours, 'hour');
  if (days < maxDays) return formatter.format(-days, 'day');

  return formatDate(date);
}

/**
 * Format a date in the active locale.
 *
 * Every other date helper funnels through here, so the `locale` escape hatch
 * below is available to all of them.
 *
 * @param {number|string|Date} value
 * @param {Object} [options] - Intl.DateTimeFormat options; defaults to the
 *   locale's short numeric date, matching the previous toLocaleDateString()
 *   calls. Pass `locale` to pin the output to a specific language instead of
 *   the active one — needed for text bound for someone else, such as an email
 *   body, where the reader's language is not the sender's.
 * @returns {string} '' for a missing or unparseable value
 */
export function formatDate(value, options) {
  if (value === null || value === undefined || value === '') return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const { locale, ...intlOptions } = options || {};

  return new Intl.DateTimeFormat(locale || activeLocale(), intlOptions).format(date);
}

/**
 * Format a date the long way — "August 26, 2026" / "26 de agosto de 2026".
 * Replaces luxon `.toFormat('MMMM d, yyyy')`, whose tokens are English-only.
 *
 * @param {number|string|Date} value
 * @param {Object} [options] - overrides merged over the long-date defaults
 */
export function formatLongDate(value, options) {
  return formatDate(value, { year: 'numeric', month: 'long', day: 'numeric', ...options });
}

/**
 * Format a date the medium way — "Aug 26, 2026" / "26 ago 2026".
 * Replaces luxon `.toFormat('MMM d, yyyy')`.
 *
 * @param {number|string|Date} value
 * @param {Object} [options] - overrides merged over the medium-date defaults
 */
export function formatMediumDate(value, options) {
  return formatDate(value, { year: 'numeric', month: 'short', day: 'numeric', ...options });
}

/**
 * Format a time of day, letting the locale decide 12- vs 24-hour.
 *
 * English gets "3:05 PM"; Spanish conventionally gets 24-hour "15:05". This is
 * what replaces convertTo12HourTime(), which hardcoded AM/PM.
 *
 * @param {number|string|Date} value
 * @param {Object} [options] - Intl.DateTimeFormat options
 */
export function formatTime(value, options) {
  return formatDate(value, { hour: 'numeric', minute: '2-digit', ...options });
}

/**
 * Format a date and time together.
 *
 * @param {number|string|Date} value
 * @param {Object} [options] - Intl.DateTimeFormat options
 */
export function formatDateTime(value, options) {
  return formatDate(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  });
}

/**
 * Format a number with the locale's grouping and decimal separators.
 *
 * Spanish uses '.' for thousands and ',' for decimals, so raw toFixed() output
 * is simply wrong there.
 *
 * @param {number} value
 * @param {Object} [options] - Intl.NumberFormat options
 * @returns {string} '' for a non-finite value
 */
export function formatNumber(value, options) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';

  return new Intl.NumberFormat(activeLocale(), options).format(value);
}

/**
 * Format a percentage from a already-scaled value — formatPercent(42.5) is
 * "42.5%". Call sites hold percentages as 0-100, not 0-1, so this does not
 * divide.
 *
 * @param {number} value - a percentage in the 0-100 range
 * @param {number} [digits=0] - fraction digits, fixed rather than ranged so
 *   output width stays stable in table columns
 * @returns {string} '' for a non-finite value
 */
export function formatPercent(value, digits = 0) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';

  return new Intl.NumberFormat(activeLocale(), {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value / 100);
}
